/**
 * Máquina de estados pura del ciclo de vida de una tool call MCP:
 * idle -> (permiso) -> (confirmación si es sensible) -> running -> completed | error | cancelled.
 * Sin dependencias de React: se testea de forma aislada y se conecta a la UI vía useToolCallRun.
 */
import type { EngineError, EngineOutput, ScenarioDefinition } from "./types";

export type ToolCallStatus =
  | "idle"
  | "awaiting-permission"
  | "permission-denied"
  | "awaiting-confirmation"
  | "running"
  | "completed"
  | "error"
  | "cancelled";

export interface ToolCallState {
  status: ToolCallStatus;
  scenario: ScenarioDefinition;
  progress: number;
  result: EngineOutput | null;
  error: EngineError | null;
}

export type ToolCallAction =
  | { type: "REQUEST" }
  | { type: "GRANT_PERMISSION" }
  | { type: "DENY_PERMISSION" }
  | { type: "CONFIRM" }
  | { type: "CANCEL_CONFIRMATION" }
  | { type: "PROGRESS"; value: number }
  | { type: "SUCCESS"; output: EngineOutput }
  | { type: "FAILURE"; error: EngineError }
  | { type: "CANCEL" }
  | { type: "RESET"; scenario: ScenarioDefinition };

export function createInitialToolCallState(scenario: ScenarioDefinition): ToolCallState {
  return { status: "idle", scenario, progress: 0, result: null, error: null };
}

function nextAfterGate(scenario: ScenarioDefinition): ToolCallStatus {
  return scenario.sensitive ? "awaiting-confirmation" : "running";
}

export function toolCallReducer(state: ToolCallState, action: ToolCallAction): ToolCallState {
  switch (action.type) {
    case "REQUEST": {
      if (state.status !== "idle" && state.status !== "permission-denied") return state;
      const status = state.scenario.requiresPermission
        ? "awaiting-permission"
        : nextAfterGate(state.scenario);
      return { ...state, status, progress: 0, result: null, error: null };
    }
    case "GRANT_PERMISSION": {
      if (state.status !== "awaiting-permission") return state;
      return { ...state, status: nextAfterGate(state.scenario) };
    }
    case "DENY_PERMISSION": {
      if (state.status !== "awaiting-permission") return state;
      return { ...state, status: "permission-denied" };
    }
    case "CONFIRM": {
      if (state.status !== "awaiting-confirmation") return state;
      return { ...state, status: "running", progress: 0 };
    }
    case "CANCEL_CONFIRMATION": {
      if (state.status !== "awaiting-confirmation") return state;
      return { ...state, status: "idle" };
    }
    case "PROGRESS": {
      if (state.status !== "running") return state;
      return { ...state, progress: Math.min(100, Math.max(0, action.value)) };
    }
    case "SUCCESS": {
      if (state.status !== "running") return state;
      return { ...state, status: "completed", progress: 100, result: action.output };
    }
    case "FAILURE": {
      if (state.status !== "running" && state.status !== "awaiting-confirmation") return state;
      return { ...state, status: "error", error: action.error };
    }
    case "CANCEL": {
      if (state.status !== "running" && state.status !== "awaiting-confirmation") return state;
      return { ...state, status: "cancelled" };
    }
    case "RESET": {
      return createInitialToolCallState(action.scenario);
    }
    default:
      return state;
  }
}
